import type { SoftphoneConnectionState } from "@dialstack/sdk-react/softphone";
import { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import type {
  EmergencyAddress,
  EmergencyAddressInput
} from "@dialstack/sdk-webrtc";

import {
  e911AddressButtonSx,
  e911BannerSx,
  e911BoundRowSx,
  e911BoundTextSx,
  e911CardTextButtonSx,
  e911ErrorSx,
  e911HeadingSx,
  e911HintSx,
  e911TextButtonSx
} from "./dialstack-softphone-widget.styles";
import E911AddressForm from "./e911-address-form.component";
import {
  dedupeEmergencyAddresses,
  formatAddress,
  shortAddressLabel
} from "./e911-address.util";

/** The server's rejection when an outbound call has no bound 911 location. */
export const EMERGENCY_REQUIRED_CODE = "emergency_address_required";

/**
 * The SDK's `useEmergencyBinding` return value, restated structurally.
 * `UseEmergencyBinding` exists in the package's types but is dropped from its
 * re-export whitelist, and `package.json` publishes no `./react/softphone`
 * subpath, so it cannot be imported.
 */
export type EmergencyBinding = {
  loading: boolean;
  bound: boolean;
  savedAddresses: EmergencyAddress[];
  submitting: boolean;
  error: string | null;
  confirm: (id: string) => Promise<void>;
  create: (input: EmergencyAddressInput) => Promise<void>;
};

type Props = {
  emergency: EmergencyBinding;
  connection: SoftphoneConnectionState;
  hasCallActivity: boolean;
  lastError: { code: string; message: string } | null;
  /**
   * Ids already removed this session — `savedAddresses` is only refetched on
   * reconnect, so a deleted row lingers in the list until then.
   */
  deletedIds: string[];
  /**
   * Asks to delete a redundant saved address. Fire-and-forget: the confirmation
   * and the removal belong to `use-e911-address-delete`, so the user may well
   * never go through with it.
   */
  onDeleteAddress: (address: EmergencyAddress) => void;
};

/**
 * One-click 911 location picker, replacing the SDK's own banner (hidden by the
 * `.ds-e911-root` rule in `dialstack-softphone-widget.styles.ts`).
 *
 * The SDK's version costs two clicks — a collapsed amber bar that must be
 * expanded before the saved addresses appear — and renders nothing at all once
 * bound, which is why the per-network memory it already keeps is invisible to
 * the user. Here every address is its own button, so confirming is one click,
 * and a bound location is stated rather than hidden.
 *
 * We keep the SDK's binding engine: `confirm`/`create` still reconnect the
 * socket with an `emergency_address_id`, which is the only thing that actually
 * binds an address.
 */
export default function E911LocationBanner({
  emergency,
  connection,
  hasCallActivity,
  lastError,
  deletedIds,
  onDeleteAddress
}: Props) {
  const { loading, bound, savedAddresses, submitting, error, confirm, create } =
    emergency;

  // Set after our own successful confirm/create. `confirm()` leaves the SDK's
  // `submitting` true across the reconnect, and its post-reconnect pass is
  // guarded by `if (!submittingRef.current)` — so `bound` can stay false until
  // the next connection transition even though the binding succeeded. Trusting
  // `bound` alone there would re-prompt a user who just answered.
  const [justBound, setJustBound] = useState<{ id: string | null } | null>(
    null
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const wasBoundRef = useRef(false);

  useEffect(() => {
    if (bound) {
      wasBoundRef.current = true;
      return;
    }
    // Losing `bound` after having had it is a `network.changed` re-attestation:
    // the device may have moved, so our optimistic state must not outlive it.
    if (wasBoundRef.current) setJustBound(null);
  }, [bound]);

  const addresses = useMemo(
    () => savedAddresses.filter((entry) => !deletedIds.includes(entry.id)),
    [savedAddresses, deletedIds]
  );
  const groups = useMemo(
    () => dedupeEmergencyAddresses(addresses),
    [addresses]
  );

  const handleConfirm = (id: string) => {
    setPendingId(id);
    confirm(id)
      .then(() => {
        setJustBound({ id });
        setPickerOpen(false);
        setFormOpen(false);
      })
      // The binding reports why through `emergency.error`.
      .catch(() => undefined)
      .finally(() => setPendingId(null));
  };

  const handleCreate = (input: EmergencyAddressInput) => {
    create(input)
      .then(() => {
        // `create` reconnects with the new id but does not return it, so there
        // is no label to show until the list refreshes.
        setJustBound({ id: null });
        setFormOpen(false);
        setPickerOpen(false);
      })
      .catch(() => undefined);
  };

  // Nothing renders mid-reconnect: `confirm`/`create` tear the socket down, and
  // the binding's `loading` flag does not reliably clear across that gap.
  if (connection !== "connected") return null;
  // Our banner sits at panel level, so unlike the SDK's it is reachable during a
  // call — and confirming reconnects, which would hang that call up.
  if (hasCallActivity) return null;
  if (loading) return null;

  const optimisticallyBound = justBound !== null && error === null;

  if ((bound || optimisticallyBound) && !pickerOpen) {
    // `emergency` never exposes the bound id, so name the location from the
    // public `registered_ip` — the provider's own record of which network an
    // address registered from.
    const registered = addresses.filter(
      (entry) => entry.registered_ip !== null
    );
    const boundAddress =
      addresses.find((entry) => entry.id === justBound?.id) ??
      (registered.length === 1 ? registered[0] : null);

    return (
      <Box role="status" aria-live="polite" data-suno-e911 sx={e911BoundRowSx}>
        <Typography sx={e911BoundTextSx}>
          {boundAddress
            ? `911 location · ${shortAddressLabel(boundAddress.address)}`
            : "911 location set"}
        </Typography>
        <Button
          variant="text"
          sx={e911TextButtonSx}
          onClick={() => {
            setPickerOpen(true);
            setShowAll(false);
          }}
        >
          Change
        </Button>
      </Box>
    );
  }

  // A bound->unbound flip while still connected is `network.changed`. `!bound`
  // matters: we also land here when a bound user taps "Change", and telling them
  // their network moved would be a lie. A failed address list also clears
  // `bound`, hence the addresses/error narrowing.
  const forcedReprompt =
    wasBoundRef.current &&
    !bound &&
    !optimisticallyBound &&
    addresses.length > 0 &&
    error === null;
  const headingText =
    addresses.length === 0
      ? "Add your 911 location"
      : forcedReprompt
        ? "Your network changed — confirm where you are"
        : "Where are you calling from?";

  const rows: { address: EmergencyAddress; duplicates: EmergencyAddress[] }[] =
    showAll
      ? addresses.map((address) => ({ address, duplicates: [] }))
      : groups;
  const collapsedSomething = groups.length < addresses.length;
  const showForm = formOpen || addresses.length === 0;

  return (
    <Box role="status" aria-live="polite" data-suno-e911 sx={e911BannerSx}>
      {lastError?.code === EMERGENCY_REQUIRED_CODE && (
        <Typography role="alert" sx={e911ErrorSx}>
          That call needs a 911 location first.
        </Typography>
      )}

      <Typography component="h2" sx={e911HeadingSx}>
        {headingText}
      </Typography>
      <Typography sx={e911HintSx}>
        Emergency services are sent here if you dial 911.
      </Typography>

      {!showForm &&
        rows.map(({ address, duplicates }) => {
          // Remove is offered only for a single collapsed duplicate: these are
          // regulatory records, so there is no bulk delete. A duplicate that is
          // still registered to a network may be what another device is bound
          // to right now, so it is explained rather than offered.
          const duplicate = duplicates.length === 1 ? duplicates[0] : null;
          const lockedToNetwork =
            duplicate !== null && duplicate.registered_ip !== null;
          return (
            <Box key={address.id}>
              <Button
                variant="text"
                sx={e911AddressButtonSx}
                disabled={submitting}
                aria-busy={pendingId === address.id}
                aria-label={`${formatAddress(
                  address.address
                )} — confirm as my 911 location`}
                onClick={() => handleConfirm(address.id)}
              >
                {pendingId === address.id
                  ? "Confirming…"
                  : formatAddress(address.address)}
              </Button>

              {duplicate && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                    pl: 1
                  }}
                >
                  <Typography sx={e911HintSx}>
                    Saved twice as {shortAddressLabel(duplicate.address)}
                    {lockedToNetwork && " — that copy is in use on a network"}
                  </Typography>
                  {!lockedToNetwork && (
                    <Button
                      variant="text"
                      sx={e911CardTextButtonSx}
                      disabled={submitting}
                      onClick={() => onDeleteAddress(duplicate)}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              )}
              {duplicates.length > 1 && (
                <Typography sx={[e911HintSx, { pl: 1 }]}>
                  {duplicates.length} similar entries hidden
                </Typography>
              )}
            </Box>
          );
        })}

      {!showForm && error && (
        <Typography role="alert" sx={e911ErrorSx}>
          {error}
        </Typography>
      )}

      {showForm && (
        <E911AddressForm
          savedAddresses={addresses}
          submitting={submitting}
          error={error}
          onCreate={handleCreate}
          onUseExisting={handleConfirm}
          onCancel={
            addresses.length > 0
              ? () => {
                  setFormOpen(false);
                }
              : null
          }
        />
      )}

      {!showForm && (
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="text"
            sx={e911CardTextButtonSx}
            onClick={() => setFormOpen(true)}
          >
            Add a different address
          </Button>
          {collapsedSomething && (
            <Button
              variant="text"
              sx={e911CardTextButtonSx}
              aria-pressed={showAll}
              onClick={() => setShowAll((current) => !current)}
            >
              {showAll ? "Hide duplicates" : "Show all saved locations"}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
